import { SpinTransportError, type SpinResponse } from "@slot/client-engine";
import type { CoreServices } from "../CoreServices";
import type { GameRuntime } from "../../composition/GameRuntime";
import type { ReplayRoundProvider } from "../../replay/ReplayRoundProvider";
import type { SystemModalPresenter } from "../../system-modal/SystemModalPresenter";
import { failureModal, flowFailure } from "../../system-modal/failureCopy";
import type { LocalizationParams } from "../../localization/LocalizationTypes";
import type { PendingRound } from "../settings/GameInitialSettingsTypes";
import type { Log } from "@slot/presentation";

export type RestoreStatus =
  | "fetching"
  | "fetch_failed"
  | "offered"
  | "declined"
  | "restoring"
  | "restored"
  | "restore_failed"
  // Decision 79: a round fetched in vain, offered as a collect; a round the server is already paying.
  | "collect_offered"
  | "collected"
  | "settling"
  | "settled"
  | "settle_deferred";

/** The pending round being restored; `status` is what the debug hook reports. */
export interface RestoreState {
  status: RestoreStatus;
  roundId: string;
}

/**
 * An interrupted round the settings report — a round cut short, or one whose settlement never
 * reached the server; the client keeps nothing across a reload, so the server's word is the only
 * one (user, 2026-09-15). Fetched first (no wait after "Continue"), its bet goes on the panel
 * through the display override, then the two-button modal. Declined → the round is
 * settled unseen. Accepted → it plays through the coordinator's restore path (no server request, no
 * gamble offer) and is settled; a failure shows the restore-error modal, whose only offer is a
 * reload.
 *
 * Decision 79 (the backend's ADR 0068: a win is paid on presentation-complete, which is safe for any round at any time
 * and pays once). The settings' `pendingRound`, when it names this round, gives the unpaid win, and the offer shows it.
 * A round the server is already paying (`settling`) is not offered at all: its settlement goes at once and its balance
 * reaches the readout; if the wallet still cannot pay (the backend's own 5xx), the boot goes on without a reload, which
 * would only repeat the failure — the next spin pays the win first, or is refused with `previous_round_unsettled`.
 * Any other failure there reloads, like every settlement's (decision 80). A round that cannot be
 * fetched is no longer skipped in silence: a one-button dialog says its win is credited, and OK settles it unseen.
 */
export async function restorePendingRound(
  restore: RestoreState,
  pendingRound: PendingRound | null,
  replayRounds: ReplayRoundProvider,
  runtime: GameRuntime,
  services: Pick<CoreServices, "localization" | "betSystem" | "moneyContext">,
  systemModalPresenter: SystemModalPresenter,
  reloadPage: () => void,
  log: Log,
): Promise<void> {
  const { roundId } = restore;
  const t = (key: string, fallback: string, params?: LocalizationParams): string => services.localization.t(key, fallback, params);
  const showRestoreFailure = (): void => {
    void systemModalPresenter.show(failureModal(flowFailure("restore"), t)).then(() => reloadPage());
  };
  // Only the settings' word for THIS round counts; a summary of another round is ignored.
  const pending = pendingRound?.roundIdentifier === roundId ? pendingRound : null;
  const amount = pending && BigInt(pending.unpaidWinMinor) > 0n ? services.moneyContext.formatter.formatMinor(pending.unpaidWinMinor) : null;
  // The amount is named only where nothing is left to reveal: once a win the player did not watch is paid.
  const showCredited = async (): Promise<void> => {
    if (!amount) return;
    await systemModalPresenter.show({ title: t("restore.credited.title", "Win Credited"), message: t("restore.credited.message", "Your win of {amount} has been credited to your balance.", { amount }), primaryButtonText: t("common.ok", "OK"), dismissible: true, cue: "soft" });
  };

  if (pending?.state === "settling") {
    restore.status = "settling";
    try {
      await runtime.settlePendingRound(roundId);
      restore.status = "settled";
      log(`[Restore] pending round settled at boot (roundId=${roundId}, its payment was under way)`);
      await showCredited();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Only the backend's own word that it could not pay yet carries on (decision 80). No answer, a session that
      // ended, an unreadable reply: an outcome the client cannot verify, and a reload (decision 17).
      if (err instanceof SpinTransportError && err.kind === "server") {
        restore.status = "settle_deferred";
        log(`[Restore] pending round settlement failed at boot (roundId=${roundId}): ${message} — the next spin pays it first`);
        return;
      }
      restore.status = "restore_failed";
      log(`[Restore] pending round settlement failed at boot (roundId=${roundId}): ${message}`);
      showRestoreFailure();
    }
    return;
  }

  let spinResponse: SpinResponse;
  try {
    spinResponse = await replayRounds.fetchRoundForReplay(roundId);
  } catch (err) {
    restore.status = "fetch_failed";
    log(`[Restore] pending round fetch failed (roundId=${roundId}): ${err instanceof Error ? err.message : String(err)} — offering to collect it unseen`);
    // Nothing to replay, but the round is still the server's to pay: a one-button dialog, then the same settlement as a
    // decline. The bet, when the settings name it, is shown as the replay's first event would show it.
    if (pending) services.betSystem.setDisplayOverride(pending.betAmountMinor);
    try {
      restore.status = "collect_offered";
      await systemModalPresenter.show({
        title: t("restore.title", "Game Interrupted"),
        message: amount
          ? t("restore.collect.message_amount", "Your previous game was interrupted and cannot be shown right now. Its win of {amount} is credited to your balance when you continue.", { amount })
          : t("restore.collect.message", "Your previous game was interrupted and cannot be shown right now. Its win is credited to your balance when you continue."),
        primaryButtonText: t("common.ok", "OK"),
        dismissible: false,
        cue: "soft",
      });
      try {
        await runtime.settlePendingRound(roundId);
        restore.status = "collected";
      } catch (settleErr) {
        restore.status = "restore_failed";
        log(`[Restore] pending round settlement failed after a failed fetch (roundId=${roundId}): ${settleErr instanceof Error ? settleErr.message : String(settleErr)}`);
        showRestoreFailure();
      }
    } finally {
      if (pending) services.betSystem.setDisplayOverride(null);
    }
    return;
  }
  const firstEvent = spinResponse.roundSnapshot.orderedEvents[0];
  // The round's bet and, for a title with lines, the lines it covered (decision 56): the next wager after it.
  if (firstEvent?.betAmountAtEvent) services.betSystem.setDisplayOverride(firstEvent.betAmountAtEvent, firstEvent.betLinesAtEvent);

  restore.status = "offered";
  const outcome = await systemModalPresenter.show({
    title: t("restore.title", "Game Interrupted"),
    // No amount here: the replay is the round's reveal, and naming the win first would spoil it (user, 2026-10-18).
    message: t("restore.message", "Your previous game was interrupted. Its win is credited to your balance either way: Continue replays the round first, No thanks skips the replay."),
    primaryButtonText: t("restore.continue", "Continue"),
    secondaryButtonText: t("restore.decline", "No thanks"),
    dismissible: false,
    cue: "soft",
  });
  const accepted = outcome === "primary";

  try {
    if (!accepted) {
      restore.status = "declined";
      log(`[Restore] pending round restore declined (roundId=${roundId})`);
      try {
        await runtime.settlePendingRound(roundId);
      } catch (err) {
        // Decision 17: a failure whose outcome the client cannot verify ends in a reload. This POST
        // may or may not have reached the server, so the round may or may not still be open. Until
        // 2026-09-21 this branch only logged and the boot carried on — unlocking the bet and showing
        // the bar over a round the server may still hold, with a balance that excludes its outcome.
        // The accepted branch below has always treated the same failure this way.
        restore.status = "restore_failed";
        log(`[Restore] pending round settlement failed after decline (roundId=${roundId}): ${err instanceof Error ? err.message : String(err)}`);
        showRestoreFailure();
        return;
      }
      // The player skipped the reveal: now the amount can be named.
      await showCredited();
      return;
    }

    restore.status = "restoring";
    try {
      await runtime.presentRestoredRound(spinResponse);
      log(`[Restore] pending round restored (roundId=${roundId})`);
      await runtime.settlePendingRound(roundId);
      restore.status = "restored";
    } catch (err) {
      restore.status = "restore_failed";
      log(`[Restore] pending round replay failed (roundId=${roundId}): ${err instanceof Error ? err.message : String(err)}`);
      showRestoreFailure();
    }
  } finally {
    // The override existed to show the interrupted round's exact bet on the modal and the bar while
    // this flow ran; the flow is over. It is display-only by name only — `getCurrentBetMinor()`
    // returns it, and that is what the next spin request, the Buy Bonus price and the extra-spins
    // cost are all built from, so an off-ladder value left here is wagered and charged (user,
    // 2026-09-21). `setDisplayOverride` already synced the ladder index to the step at or below it,
    // so the readout settles there and the request carries the same amount.
    services.betSystem.setDisplayOverride(null);
  }
}
